import type React from 'react';
import Input from '../Input';
import { EDUCATION_LEVELS } from './constants';
import type {
  ScholarshipFormData,
  ScholarshipEducationLevel,
  FormErrors,
} from './types';

interface StepEducationProps {
  values: ScholarshipFormData;
  errors: FormErrors;
  onChange: (name: keyof ScholarshipFormData, value: string) => void;
  onEducationLevelChange: (level: ScholarshipEducationLevel) => void;
  onBlur: (name: keyof ScholarshipFormData) => void;
}

export default function StepEducation({
  values,
  errors,
  onChange,
  onEducationLevelChange,
  onBlur,
}: StepEducationProps) {
  const isSchool =
    values.education_level === 'high_school_student' ||
    values.education_level === 'high_school_graduate';

  const isUniversity =
    values.education_level === 'university_student' ||
    values.education_level === 'university_graduate';

  const handleChange =
    (name: keyof ScholarshipFormData) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      onChange(name, e.target.value);
    };

  const handleBlur = (name: keyof ScholarshipFormData) => () => {
    onBlur(name);
  };

  const motivationLength = values.motivation.length;
  const isMotivationOver = motivationLength > 800;

  const achievementsLength = values.achievements.length;
  const isAchievementsOver = achievementsLength > 500;

  return (
    <div className="space-y-6">
      {/* Title & Subtitle */}
      <div>
        <h2 className="text-xl sm:text-2xl font-bold text-[#2C2C2C] mb-2">
          احكيلنا عن دراستك
        </h2>
        <p className="text-sm text-[#707070] leading-relaxed">
          اختار مؤهلك الدراسي واكتب تفاصيل دراستك والمجال اللي نفسك تدرسه.
        </p>
      </div>

      {/* Education Level Selector - Tappable Option Cards */}
      <div>
        <label className="block text-sm font-semibold text-[#2C2C2C] mb-2.5">
          المؤهل الدراسي <span className="text-[#F46A6A] ms-1">*</span>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {EDUCATION_LEVELS.map((level) => {
            const isSelected = values.education_level === level.value;
            return (
              <button
                key={level.value}
                name="education_level"
                type="button"
                onClick={() => onEducationLevelChange(level.value)}
                className={`
                  flex items-center justify-between p-4 rounded-xl border-2 text-start transition-all duration-200 min-h-[52px]
                  ${
                    isSelected
                      ? 'border-[#5B3FD0] bg-[#5B3FD0]/5 text-[#5B3FD0] shadow-sm'
                      : 'border-gray-200 bg-white hover:border-[#5B3FD0]/40 text-[#2C2C2C]'
                  }
                `}
              >
                <span className="font-semibold text-sm sm:text-base">
                  {level.label}
                </span>
                <span
                  className={`
                    w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors
                    ${
                      isSelected
                        ? 'border-[#5B3FD0] bg-[#5B3FD0]'
                        : 'border-gray-300 bg-white'
                    }
                  `}
                >
                  {isSelected && (
                    <span className="w-2 h-2 rounded-full bg-white" />
                  )}
                </span>
              </button>
            );
          })}
        </div>
        {errors.education_level && (
          <p className="mt-2 text-sm text-[#F46A6A] flex items-center gap-1">
            <span>{errors.education_level}</span>
          </p>
        )}
      </div>

      {/* School Fields */}
      {isSchool && (
        <div className="space-y-4 pt-2 border-t border-gray-100">
          <Input
            name="school_name"
            label="اسم المدرسة"
            required
            placeholder="مثال: مدرسة السعيدية الثانوية"
            value={values.school_name}
            error={errors.school_name}
            onChange={handleChange('school_name')}
            onBlur={handleBlur('school_name')}
            className="text-base"
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              name="graduation_year"
              label="سنة التخرج"
              required
              type="number"
              inputMode="numeric"
              dir="ltr"
              placeholder="2025"
              value={values.graduation_year}
              error={errors.graduation_year}
              onChange={handleChange('graduation_year')}
              onBlur={handleBlur('graduation_year')}
              className="text-start text-base"
            />
            <Input
              name="academic_grade"
              label="النسبة / المجموع"
              required
              placeholder="مثال: 95% أو 390"
              value={values.academic_grade}
              error={errors.academic_grade}
              onChange={handleChange('academic_grade')}
              onBlur={handleBlur('academic_grade')}
              className="text-base"
            />
          </div>
        </div>
      )}

      {/* University Fields */}
      {isUniversity && (
        <div className="space-y-4 pt-2 border-t border-gray-100">
          <Input
            name="university_name"
            label="اسم الجامعة"
            required
            placeholder="مثال: جامعة القاهرة"
            value={values.university_name}
            error={errors.university_name}
            onChange={handleChange('university_name')}
            onBlur={handleBlur('university_name')}
            className="text-base"
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              name="faculty"
              label="الكلية"
              required
              placeholder="مثال: كلية الهندسة"
              value={values.faculty}
              error={errors.faculty}
              onChange={handleChange('faculty')}
              onBlur={handleBlur('faculty')}
              className="text-base"
            />
            <Input
              name="major"
              label="التخصص"
              placeholder="مثال: هندسة حاسبات (اختياري)"
              value={values.major}
              error={errors.major}
              onChange={handleChange('major')}
              onBlur={handleBlur('major')}
              className="text-base"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input
              name="graduation_year"
              label="سنة التخرج أو السنة المتوقعة"
              required
              type="number"
              inputMode="numeric"
              dir="ltr"
              placeholder="2027"
              value={values.graduation_year}
              error={errors.graduation_year}
              onChange={handleChange('graduation_year')}
              onBlur={handleBlur('graduation_year')}
              className="text-start text-base"
            />
            <Input
              name="academic_grade"
              label="التقدير / GPA / النسبة"
              required
              placeholder="مثال: 3.4 GPA أو امتياز"
              value={values.academic_grade}
              error={errors.academic_grade}
              onChange={handleChange('academic_grade')}
              onBlur={handleBlur('academic_grade')}
              className="text-base"
            />
          </div>
        </div>
      )}

      {/* Shared Fields (Preferred study field + Motivation + Achievements) */}
      <div className="space-y-4 pt-2 border-t border-gray-100">
        <Input
          name="preferred_study_field"
          label="التخصص اللي حابب تدرسه"
          required
          placeholder="مثال: علوم الحاسب والذكاء الاصطناعي"
          value={values.preferred_study_field}
          error={errors.preferred_study_field}
          onChange={handleChange('preferred_study_field')}
          onBlur={handleBlur('preferred_study_field')}
          className="text-base"
        />

        {/* Motivation Question */}
        <div>
          <label className="block text-sm font-semibold text-[#2C2C2C] mb-1">
            ليه شايف إنك تستحق المنحة؟ <span className="text-[#F46A6A] ms-1">*</span>
          </label>
          <p className="text-xs text-[#707070] mb-2">جملتين تلاتة كفاية.</p>
          <textarea
            name="motivation"
            rows={4}
            value={values.motivation}
            onChange={handleChange('motivation')}
            onBlur={handleBlur('motivation')}
            placeholder="احكيلنا باختصار عن شغفك وأهدافك وليه المنحة دي هتفرق في مستقبلك..."
            className={`
              w-full px-4 py-3 rounded-xl border-2 transition-all duration-200 text-base
              focus:outline-none resize-y placeholder:text-[#707070]/50
              ${
                errors.motivation || isMotivationOver
                  ? 'border-[#F46A6A] focus:border-[#F46A6A] focus:ring-4 focus:ring-[#F46A6A]/10'
                  : 'border-gray-200 focus:border-[#5B3FD0] focus:ring-4 focus:ring-[#5B3FD0]/10'
              }
            `}
          />
          <div className="flex items-center justify-between mt-1 text-xs">
            {errors.motivation ? (
              <span className="text-[#F46A6A]">{errors.motivation}</span>
            ) : (
              <span className="text-[#707070]">الحد الأدنى 60 حرفًا</span>
            )}
            <span
              dir="ltr"
              className={`font-mono text-xs ${
                isMotivationOver ? 'text-[#F46A6A] font-bold' : 'text-[#707070]'
              }`}
            >
              {motivationLength}/800
            </span>
          </div>
        </div>

        {/* Achievements Question */}
        <div>
          <label className="block text-sm font-semibold text-[#2C2C2C] mb-2">
            عندك إنجاز أو نشاط مميز؟ <span className="text-xs font-normal text-[#707070] ms-1">(اختياري)</span>
          </label>
          <textarea
            name="achievements"
            rows={3}
            value={values.achievements}
            onChange={handleChange('achievements')}
            onBlur={handleBlur('achievements')}
            placeholder="جوائز، مسابقات، أنشطة تطوعية، أو مشاريع عملتها..."
            className={`
              w-full px-4 py-3 rounded-xl border-2 transition-all duration-200 text-base
              focus:outline-none resize-y placeholder:text-[#707070]/50
              ${
                errors.achievements || isAchievementsOver
                  ? 'border-[#F46A6A] focus:border-[#F46A6A] focus:ring-4 focus:ring-[#F46A6A]/10'
                  : 'border-gray-200 focus:border-[#5B3FD0] focus:ring-4 focus:ring-[#5B3FD0]/10'
              }
            `}
          />
          <div className="flex items-center justify-between mt-1 text-xs">
            {errors.achievements && (
              <span className="text-[#F46A6A]">{errors.achievements}</span>
            )}
            <span
              dir="ltr"
              className={`font-mono text-xs ms-auto ${
                isAchievementsOver ? 'text-[#F46A6A] font-bold' : 'text-[#707070]'
              }`}
            >
              {achievementsLength}/500
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
